
import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Student } from "@/types";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { AutosuggestInput } from "@/components/nurse/AutosuggestInput";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface StudentVisitDialogProps {
  student: Student | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onVisitSaved?: () => void;
}

export function StudentVisitDialog({ student, open, onOpenChange, onVisitSaved }: StudentVisitDialogProps) {
  const [complaint, setComplaint] = useState("");
  const [complaints, setComplaints] = useState<string[]>([]);
  const [diagnosis, setDiagnosis] = useState("");
  const [diagnoses, setDiagnoses] = useState<string[]>([]);
  const [temperature, setTemperature] = useState("36.2");
  const [age, setAge] = useState("");
  const [treatment, setTreatment] = useState("");
  const [treatments, setTreatments] = useState<string[]>([]);
  const [itemToRemove, setItemToRemove] = useState<{ type: "complaint" | "diagnosis" | "treatment"; value: string } | null>(null);
  const [ageWarningOpen, setAgeWarningOpen] = useState(false);
  const [pendingAge, setPendingAge] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (student?.age) {
      setAge(student.age.toString());
    } else {
      setAge("");
    }
  }, [student]);

  const { data: nurseProfile } = useQuery({
    queryKey: ["nurseProfile", user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error("User ID not found");
      const { data, error } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", user.id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const normalizeText = (text: string) => text.toLowerCase().trim().replace(/\s+/g, " ");
  const isDuplicate = (item: string, list: string[]) => list.some(existing => normalizeText(existing) === normalizeText(item));

  const handleAgeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newAge = e.target.value;
    if (student?.age !== undefined && student.age.toString() !== newAge) {
      setPendingAge(newAge);
      setAgeWarningOpen(true);
    } else {
      setAge(newAge);
    }
  };

  const handleAgeWarningConfirm = async () => {
    if (!student?.id) return;

    try {
      const { error } = await supabase
        .from("students")
        .update({ age: parseInt(pendingAge, 10) })
        .eq("id", student.id);

      if (error) throw error;

      setAge(pendingAge);
      setAgeWarningOpen(false);
      
      toast({
        title: "Age Updated",
        description: "The student's age has been updated.",
      });

      await queryClient.invalidateQueries({ 
        queryKey: ["students"]
      });
    } catch (error) {
      console.error("Error updating age:", error);
      toast({
        title: "Error",
        description: "Failed to update student's age",
        variant: "destructive",
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!student || isSubmitting || !nurseProfile?.id || !user?.schoolId) return;
    setIsSubmitting(true);

    try {
      if (age && student.age !== parseInt(age)) {
        const { error: updateError } = await supabase
          .from("students")
          .update({ age: parseInt(age, 10) })
          .eq("id", student.id);
        if (updateError) throw updateError;
      }

      const { data: activeYear } = await supabase
        .from("academic_years")
        .select("id")
        .eq("is_active", true)
        .single();

      if (!activeYear?.id) throw new Error("No active academic year found");

      const { data: activeTerm } = await supabase
        .from("terms")
        .select("id")
        .eq("is_active", true)
        .eq("academic_year_id", activeYear.id)
        .single();

      if (!activeTerm?.id) throw new Error("No active term found");

      // Create a single visit record with all diagnoses joined by commas
      const { error } = await supabase
        .from("visits")
        .insert([{
          student_id: student.id,
          complaint: complaints.join(", "),
          diagnosis: diagnoses.join(", "),
          temperature: parseFloat(temperature),
          treatment: treatments.join(", "),
          nurse_id: nurseProfile.id,
          academic_year_id: activeYear.id,
          term_id: activeTerm.id,
          school_id: user.schoolId,
          date: new Date().toISOString(),
        }]);

      if (error) throw error;

      setComplaints([]);
      setDiagnoses([]);
      setTreatments([]);
      setTemperature("36.2");

      onOpenChange(false);
      onVisitSaved?.();

      toast({ title: "Success", description: "Visit recorded successfully" });

      await queryClient.invalidateQueries({ 
        queryKey: ["visits", user.schoolId, activeYear.id, activeTerm.id]
      });

    } catch (error) {
      console.error("Error recording visit:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to record visit",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!student) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogTitle>
            New Visit for {student.surname}
            {student.other_names ? `, ${student.other_names}` : ""}
          </DialogTitle>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Temperature (°C)</label>
                <Input 
                  type="number" 
                  step="0.1" 
                  required 
                  value={temperature} 
                  onChange={e => setTemperature(e.target.value)} 
                  placeholder="37.0" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Age</label>
                <Input 
                  type="number" 
                  value={age} 
                  onChange={handleAgeChange} 
                  placeholder="Enter age" 
                  className="w-full" 
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Complaint</label>
              <AutosuggestInput
                type="COMPLAINT"
                value={complaint}
                onChange={setComplaint}
                placeholder="Enter complaint"
                onAddItem={(item) => {
                  if (!isDuplicate(item, complaints)) {
                    setComplaints([...complaints, item]);
                  } else {
                    toast({
                      title: "Duplicate Entry",
                      description: "This complaint has already been added.",
                      variant: "destructive",
                    });
                  }
                }}
                selectedItems={complaints}
                onRemoveItem={(item) => setItemToRemove({ type: 'complaint', value: item })}
                shortcutKey="c"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Diagnosis</label>
              <AutosuggestInput
                type="DIAGNOSIS"
                value={diagnosis}
                onChange={setDiagnosis}
                placeholder="Enter diagnosis"
                onAddItem={(item) => {
                  if (!isDuplicate(item, diagnoses)) {
                    setDiagnoses([...diagnoses, item]);
                  } else {
                    toast({
                      title: "Duplicate Entry",
                      description: "This diagnosis has already been added.",
                      variant: "destructive",
                    });
                  }
                }}
                selectedItems={diagnoses}
                onRemoveItem={(item) => setItemToRemove({ type: 'diagnosis', value: item })}
                shortcutKey="d"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Treatment</label>
              <AutosuggestInput
                type="TREATMENT"
                value={treatment}
                onChange={setTreatment}
                placeholder="Enter treatment"
                onAddItem={(item) => {
                  if (!isDuplicate(item, treatments)) {
                    setTreatments([...treatments, item]);
                  } else {
                    toast({
                      title: "Duplicate Entry",
                      description: "This treatment has already been added.",
                      variant: "destructive",
                    });
                  }
                }}
                selectedItems={treatments}
                onRemoveItem={(item) => setItemToRemove({ type: 'treatment', value: item })}
                shortcutKey="t"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                Record Visit
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!itemToRemove} onOpenChange={(open) => !open && setItemToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Removal</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this {itemToRemove?.type}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              if (itemToRemove?.type === 'complaint') {
                setComplaints(complaints.filter(i => i !== itemToRemove.value));
              } else if (itemToRemove?.type === 'diagnosis') {
                setDiagnoses(diagnoses.filter(i => i !== itemToRemove.value));
              } else if (itemToRemove?.type === 'treatment') {
                setTreatments(treatments.filter(i => i !== itemToRemove.value));
              }
              setItemToRemove(null);
            }}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={ageWarningOpen} onOpenChange={setAgeWarningOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Age Update</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to update the student's age from {student.age} to {pendingAge}? This will update the student's record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setAge(student.age?.toString() || "");
              setAgeWarningOpen(false);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleAgeWarningConfirm}>
              Update Age
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
